// src/pages/ia-paca/sophia-antipolis.tsx
//
// Page géo — Sophia Antipolis (hub tech historique PACA).
// Stratégie : capter "recrutement IA Sophia Antipolis", "AI Engineer
// Sophia", "Pôle Alpha", "3IA Côte d'Azur".

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/layout';
import IaPacaHero from '../../components/ia-paca/IaPacaHero';
import IaPacaCTA from '../../components/ia-paca/IaPacaCTA';
import IaSalaryTable from '../../components/ia-paca/IaSalaryTable';
import {
  ArrowLeft,
  ArrowRight,
  Cpu,
  GraduationCap,
  Building2,
  Microscope,
  MapPin,
} from 'lucide-react';

export default function SophiaAntipolisIaPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Recruteurs.io — Recrutement IA Sophia Antipolis',
    description:
      "Cabinet de recrutement IA à Sophia Antipolis. Profils AI Engineer, MLOps, ML Research pour les 2 500 entreprises tech de Sophia et de la Côte d'Azur.",
    url: 'https://recruteurs.io/ia-paca/sophia-antipolis',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Sophia Antipolis',
      addressRegion: "Provence-Alpes-Côte d'Azur",
      addressCountry: 'FR',
    },
    areaServed: { '@type': 'City', name: 'Sophia Antipolis' },
  };

  return (
    <Layout
      title="Recrutement IA à Sophia Antipolis — AI Engineer, MLOps | recruteurs.io"
      description="Cabinet de recrutement IA à Sophia Antipolis. 2 500 entreprises tech, 38 000 emplois, Pôle Alpha, 3IA Côte d'Azur, EURECOM. Cabinet local pour AI Engineer, MLOps, ML Research, Data Scientist."
      keywords="recrutement IA Sophia Antipolis, AI Engineer Sophia, MLOps Sophia Antipolis, Pôle Alpha IA, 3IA Côte d'Azur, recrutement intelligence artificielle Côte d'Azur"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'Sophia Antipolis', href: '/ia-paca/sophia-antipolis' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <IaPacaHero
        eyebrow="Recrutement IA · Sophia Antipolis"
        title="Recruter en IA"
        highlight="à Sophia Antipolis."
        subtitle="2 500 entreprises tech, 38 000 emplois, 5 500 chercheurs. Sophia est le hub tech historique de la France et un des plus denses d'Europe sur l'IA. Avec le Pôle Alpha inauguré en 2026, la ville franchit une étape. Notre cabinet recrute pour les corporates R&D, scale-ups et spin-offs académiques de la zone."
        badges={['Pôle Alpha', '3IA Côte d\'Azur', 'EURECOM', 'House of AI', 'Cyber Campus']}
      />

      <div className="bg-white pt-6">
        <div className="mx-auto max-w-4xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Toutes les villes IA en PACA
          </Link>
        </div>
      </div>

      {/* Marché Sophia */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Marché tech & IA — Sophia Antipolis 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Sophia, capitale française de l'IA appliquée à la recherche.
          </h2>
          <div className="prose prose-slate mt-6 max-w-none text-base leading-relaxed text-slate-700">
            <p>
              <strong>Sophia Antipolis concentre 2 500 entreprises et
              38 000 emplois directs</strong>, avec 5 500 chercheurs sur
              site. C'est le premier parc technologique d'Europe, et un
              écosystème probablement le plus dense d'Europe pour l'IA et
              ses applications industrielles (cybersécurité, télécoms, IoT,
              santé, automobile).
            </p>
            <p>
              L'inauguration du <strong>Pôle Alpha en janvier 2026</strong>{' '}
              (8 500 m², incubateurs, écoles, House of AI, Cyber Campus
              Sophia Antipolis) marque un nouveau cycle. La Région Sud y a
              alloué <strong>70 M€ sur 5 ans</strong> pour structurer
              gouvernance IA, formation, et attractivité.
            </p>
            <p>
              Pour les recruteurs, Sophia présente un profil unique : forte
              concentration de PhD et profils ML Research (vs Marseille plus
              "produit"), mais aussi tension salariale élevée (compétition
              avec Amadeus, IBM, Renesas, Toyota Connected, Symphony Retail).
            </p>
          </div>
        </div>
      </section>

      {/* Écosystème Sophia */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Écosystème Sophia
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Pôle Alpha, 3IA Côte d'Azur, EURECOM — l'infrastructure complète.
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
            {ECOSYSTEME_SOPHIA.map((e) => (
              <div
                key={e.nom}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-center gap-2">
                  <e.Icon className="h-5 w-5 text-violet-600" />
                  <h3 className="text-base font-bold text-slate-900">{e.nom}</h3>
                </div>
                <p className="mt-2 text-sm text-slate-600">{e.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profils types Sophia */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Qui recrute en IA à Sophia ?
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les 3 typologies dominantes en 2026.
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            <TypologieCard
              Icon={Building2}
              tag="Corporates R&D"
              title="ETI / grands groupes tech installés"
              body="Amadeus, IBM Research, Renesas, Toyota Connected, Symphony Retail, Texas Instruments... Recrutent en ML Research, Computer Vision, NLP avancé. Process long, packages compétitifs, exigence académique."
            />
            <TypologieCard
              Icon={Microscope}
              tag="Spin-offs & DeepTech"
              title="Sorties INRIA, EURECOM, 3IA Côte d'Azur"
              body="Startups issues des labos (Inria Sophia, EURECOM, 3IA Côte d'Azur). Souvent en seed/série A. Recherchent des profils mixtes recherche/ingénierie. Niveau d'exigence scientifique élevé, équipes très tech."
            />
            <TypologieCard
              Icon={Cpu}
              tag="PME tech & IoT"
              title="Cybersécurité, IoT, télécoms IA"
              body="Sophia a une tradition forte en cybersec et IoT. Les PME locales intègrent maintenant l'IA dans leurs produits : détection d'anomalies, prédictif, agents intelligents. Stack mature, profils confirmés demandés."
            />
          </div>
        </div>
      </section>

      {/* Salaires Sophia */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires IA Sophia — mars 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Sophia, le hub tech le mieux payé du sud.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            La tension salariale à Sophia (concurrence Amadeus, IBM, Renesas)
            tire les rémunérations IA +5% au-dessus de Marseille / Aix. Le
            coût de vie sur la Côte d'Azur (logement, transport) est aussi
            plus élevé.
          </p>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            FAQ Sophia Antipolis
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les questions du marché tech local.
          </h2>
          <div className="mt-8 space-y-3">
            {FAQ_SOPHIA.map((q) => (
              <details
                key={q.question}
                className="group rounded-2xl border border-slate-200 bg-white p-5"
              >
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-base font-semibold text-slate-900 [&::-webkit-details-marker]:hidden">
                  <span>{q.question}</span>
                  <span className="mt-0.5 text-slate-400 transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <div className="mt-4 text-sm leading-relaxed text-slate-600">
                  {q.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Lien Marseille */}
      <section className="bg-slate-50 py-12">
        <div className="mx-auto max-w-4xl px-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-pink-600" />
              <div className="flex-1">
                <h3 className="text-base font-bold text-slate-900">
                  Vous recrutez aussi sur Marseille / l'agglo AMP ?
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Notre page dédiée :{' '}
                  <Link
                    to="/ia-paca/marseille"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Recrutement IA à Marseille
                  </Link>
                  .{' '}
                  <Link
                    to="/ia-paca"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Vue d'ensemble PACA
                  </Link>
                  .
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </div>
          </div>
        </div>
      </section>

      <IaPacaCTA
        title="Vous recrutez à Sophia Antipolis ?"
        subtitle="On connaît la zone, les acteurs, les contraintes salariales. Brief 30 min, court-list en 15-20 jours selon la rareté du profil."
      />
    </Layout>
  );
}

function TypologieCard({
  Icon,
  tag,
  title,
  body,
}: {
  Icon: typeof Building2;
  tag: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-violet-300 hover:shadow-md">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
        <Icon className="h-5 w-5" />
      </div>
      <span className="mt-3 inline-block rounded-full bg-pink-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-pink-700">
        {tag}
      </span>
      <h3 className="mt-2 text-base font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

const ECOSYSTEME_SOPHIA = [
  {
    nom: 'Pôle Alpha',
    Icon: Building2,
    description:
      "Inauguré janvier 2026. 8 500 m². Réunit incubateurs, écoles d'ingénieurs, structures d'accompagnement et entreprises. Abrite la House of Artificial Intelligence et le Cyber Campus Sophia Antipolis.",
  },
  {
    nom: '3IA Côte d\'Azur',
    Icon: Microscope,
    description:
      "Institut interdisciplinaire en IA (un des 4 français labellisés). Recherche fondamentale + applications. Source de profils PhD IA et de spin-offs deeptech.",
  },
  {
    nom: 'EURECOM',
    Icon: GraduationCap,
    description:
      "École d'ingénieurs reconnue pour son expertise IA et cybersécurité. Forte employabilité, profils techniques pointus, alumni nombreux dans les entreprises de Sophia.",
  },
  {
    nom: 'TechForward',
    Icon: Cpu,
    description:
      "Accélérateur installé au Pôle Alpha. Suit les startups deeptech, IA, cybersécurité. Bonne porte d'entrée pour identifier les jeunes pousses en croissance.",
  },
  {
    nom: 'Provence Côte d\'Azur Accélérateur',
    Icon: Building2,
    description:
      "Accélérateur régional avec une forte verticale IA depuis 2025. Pilote des programmes thématiques en lien avec Bpifrance et la Région Sud.",
  },
  {
    nom: 'INRIA Sophia',
    Icon: Microscope,
    description:
      "Centre Inria majeur (1000+ chercheurs). Vivier historique de talents IA, mais aussi source de spin-offs technologiques (Insilico Medicine, etc.).",
  },
];

const FAQ_SOPHIA = [
  {
    question: "Combien d'AI Engineers / ML Engineers disponibles à Sophia ?",
    answer:
      "Sophia compte un pool estimé à 800-1 200 profils IA expérimentés (AI/ML Engineers, Data Scientists confirmés, MLOps), répartis entre les corporates (Amadeus, IBM, Renesas), les startups deeptech et les freelances. Sur ce pool, 25-35% sont en veille passive et peuvent être approchés sur le bon poste.",
  },
  {
    question:
      "Pourquoi Sophia est plus chère que Marseille pour recruter en IA ?",
    answer:
      "Trois raisons : (1) concurrence active entre 5-10 gros employeurs tech sur le même bassin (Amadeus, IBM, Renesas, Toyota Connected, Symphony), (2) coût de vie Côte d'Azur (logement +30-40% vs Marseille), (3) anglais business indispensable (corporates internationaux) → réduit le pool de candidats locaux. Résultat : packages IA Sophia +5 à +10% vs Marseille.",
  },
  {
    question:
      "Faut-il être présent sur Sophia pour recruter, ou peut-on travailler à distance ?",
    answer:
      "Pour les profils Sophia, le présentiel local est encore valorisé — la culture du parc technologique reste 'sur site'. Le remote partiel (2-3j) marche, le full remote depuis Paris est plus difficile à vendre. Notre cabinet est mobile en PACA et peut venir sur place pour les briefs et points clés.",
  },
  {
    question:
      "Vous travaillez aussi avec les spin-offs INRIA / EURECOM / 3IA ?",
    answer:
      "Oui. Les spin-offs académiques ont des besoins très spécifiques : profils ML Research, capacité à prototyper rapidement, accepter une équipe petite. On a une bonne lecture de ce que recherchent ces entreprises (souvent un Lead/Co-fondateur tech + 1-2 profils seniors mixtes recherche/ingé).",
  },
  {
    question:
      "Recrutez-vous sur Nice / Cannes / Antibes en plus de Sophia ?",
    answer:
      "Oui, notre périmètre Côte d'Azur couvre Sophia + Nice + Antibes + Cannes + Cagnes-sur-Mer. Nice a sa propre dynamique (FinTech, gaming) — nous y avons une page dédiée en construction. Pour l'instant, nous traitons les besoins Nice via les pages Sophia et le hub PACA.",
  },
];
